import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase.ts';
import { StaffProvider, useStaff } from '../../contexts/StaffContext.tsx';
import type { Session } from '@supabase/supabase-js';

function StaffShellInner() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const { activeMembership, memberships, setActiveMembership } = useStaff();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s) navigate('/staff/login', { replace: true });
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Still resolving session
  if (session === undefined) {
    return (
      <div className="flex min-h-dvh items-center justify-center" style={{ background: 'var(--bg)' }}>
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Загрузка…</span>
      </div>
    );
  }

  if (!session) return <Navigate to="/staff/login" replace />;

  const tabs: Array<{ path: string; label: string; icon: string }> = [
    { path: '/staff/kitchen', label: 'Кухня', icon: '🍳' },
    { path: '/staff/stoplist', label: 'Стоп-лист', icon: '🚫' },
    { path: '/staff/stats', label: 'Статистика', icon: '📊' },
  ];

  const currentPath = location.pathname;

  async function handleLogout() {
    await supabase.auth.signOut();
  }

  return (
    <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
      {/* Top bar: tenant selector + logout */}
      <div
        className="flex items-center gap-2 px-4 py-2 border-b"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
      >
        {memberships.length > 1 ? (
          <select
            value={activeMembership?.tenant_id ?? ''}
            onChange={(e) => {
              const m = memberships.find((m) => m.tenant_id === e.target.value);
              if (m) setActiveMembership(m);
            }}
            className="flex-1 rounded-lg border px-2 py-1 text-sm"
            style={{
              background: 'var(--bg)',
              color: 'var(--text)',
              borderColor: 'var(--border)',
            }}
            aria-label="Выбрать заведение"
          >
            {memberships.map((m) => (
              <option key={m.tenant_id} value={m.tenant_id}>
                {m.tenant_name} ({m.role})
              </option>
            ))}
          </select>
        ) : (
          <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
            {activeMembership?.tenant_name ?? '—'}
            {activeMembership && (
              <span className="ml-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                ({activeMembership.role})
              </span>
            )}
          </span>
        )}

        <button
          onClick={handleLogout}
          className="rounded-lg px-3 py-1 text-xs font-medium"
          style={{ background: 'var(--n-100)', color: 'var(--text-muted)' }}
        >
          Выйти
        </button>
      </div>

      {/* Page content */}
      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: 64 }}>
        <Outlet />
      </div>

      {/* Bottom tab bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 flex border-t"
        style={{
          background: 'var(--surface)',
          borderColor: 'var(--border)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        aria-label="Навигация персонала"
      >
        {tabs.map((tab) => {
          const active = currentPath.startsWith(tab.path);
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className="flex flex-1 flex-col items-center gap-0.5 py-2 text-xs font-medium transition-colors"
              style={{ color: active ? 'var(--accent)' : 'var(--text-muted)', minHeight: 56 }}
              aria-current={active ? 'page' : undefined}
            >
              <span className="text-lg leading-none" aria-hidden="true">{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export function StaffShell() {
  return (
    <StaffProvider>
      <StaffShellInner />
    </StaffProvider>
  );
}
